// Academy articles for the Help Center.

const academy = {
  slug: 'academy',
  label: 'Academy',
  icon: 'PiBookOpen',
  description: 'Courses, live seminars, tutorials, and podcasts from fellow students.',
  articles: [
    {
      slug: 'browsing-courses-and-seminars',
      title: 'Browsing courses and seminars',
      summary: 'Academy has courses, live seminars, short tutorials, and podcasts.',
      body: [
        {
          type: 'p',
          text: 'Academy is where students teach other students. It includes full self paced courses, live seminars held over video, short tutorials, and podcasts. Open Academy from the main menu and use the tabs to browse each type.',
        },
        {
          type: 'p',
          text: 'Every course or seminar page shows what it covers, who is teaching it, and its price. Some content is free, some has a one time price, shown clearly before you enroll.',
        },
      ],
    },
    {
      slug: 'enrolling-in-a-course',
      title: 'Enrolling in a course',
      summary: 'Pay once if the course has a price, then it stays in your library.',
      body: [
        {
          type: 'p',
          text: 'Open a course and select Enroll. If the course is free, you get instant access. If it has a price, you will pay through the same secure checkout used for the rest of the site.',
        },
        {
          type: 'p',
          text: 'Once you are enrolled, the course appears in My Learning, where you can pick up where you left off at any time.',
        },
      ],
    },
    {
      slug: 'joining-a-live-seminar',
      title: 'Joining a live seminar',
      summary: 'Seminars run over video at a scheduled time, with a join link that opens shortly before it starts.',
      body: [
        {
          type: 'p',
          text: 'A seminar is a live session, not a video you watch whenever you want. After you enroll, the seminar page shows the scheduled date and time. A join button becomes active shortly before the session starts.',
        },
        {
          type: 'p',
          text: 'You do not need to install anything separately. The session opens in your browser directly from the site.',
        },
      ],
    },
    {
      slug: 'tracking-your-progress',
      title: 'Tracking your progress',
      summary: 'Courses are broken into modules, and you can see what you have finished at a glance.',
      body: [
        {
          type: 'p',
          text: 'A course is organized into modules, each made up of smaller items like readings, videos, or assignments. As you complete items, your progress is saved automatically, so you can leave and come back without losing your place.',
        },
        {
          type: 'p',
          text: 'Some courses release modules in order, so you need to finish one before the next unlocks. Others let you move through the material freely. You will see which kind a course is once you open it.',
        },
        {
          type: 'p',
          text: 'Grades has its own section in My Learning, right next to Courses. Pick a course there to see how many modules you have completed, along with every activity and discussion post you have submitted for it.',
        },
        {
          type: 'p',
          text: 'For anything you have already submitted, whether it was graded, is still pending review, or came back needing changes, you can open it to see your original answers again, not just the final score.',
        },
      ],
    },
    {
      slug: 'teaching-a-course',
      title: 'Teaching a course',
      summary: 'Build a course out of modules and items, then make it live for students to enroll in.',
      body: [
        {
          type: 'p',
          text: 'Open your Instructor Dashboard from the account menu at the top of any page. It shows every course you teach, how many students are enrolled, and anything waiting on you, like pending submissions to grade.',
        },
        {
          type: 'image',
          src: '/images/help/academy/instructor-dashboard.png', width: 880, height: 614,
          alt: 'Instructor Dashboard overview: total courses, total students, and pending submissions',
          caption: 'Instructor Dashboard, Overview tab',
        },
        {
          type: 'p',
          text: 'Select Create New Course, or Edit an existing one, to open the course builder. Your outline sits on the left, the editor for whatever you have selected is in the middle, and a live preview of exactly what students will see updates on the right as you type.',
        },
        {
          type: 'image',
          src: '/images/help/academy/course-builder.png', width: 1200, height: 616,
          alt: 'Course builder: module outline on the left, editor in the middle, live student preview on the right',
          caption: 'Course builder, Course details step',
        },
        {
          type: 'p',
          text: 'Inside a module, use Add item to build out its content: an overview video, a set of learning materials, a graded activity, a quiz, or one of the other item types. Add as many modules and items as your course needs.',
        },
        {
          type: 'image',
          src: '/images/help/academy/course-builder-add-item.png', width: 1200, height: 616,
          alt: 'The "Choose a type" item picker, showing Overview, Learning Materials, Activity, and Archetype Quiz',
          caption: 'Adding an item to a module',
        },
        {
          type: 'p',
          text: 'When it is ready, save your changes and publish. Your course appears in the Academy catalog for students to enroll in, in the same structure you laid out. If students submit assignments, you will find them waiting for review in your Instructor Dashboard.',
        },
        {
          type: 'image',
          src: '/images/help/academy/course-published.png', width: 1200, height: 527,
          alt: 'A published course page as students see it in the Academy catalog',
          caption: 'The published course, as students see it',
        },
        {
          type: 'note',
          text: 'You can promote a standout student to mentor for your course, which can unlock mentor-only content and let you message mentors as their own group. Nothing about their own enrollment changes otherwise.',
        },
      ],
    },
    {
      slug: 'ai-assisted-grading',
      title: 'Getting AI grading suggestions',
      summary: 'A starting point for written answers, which you review and accept or change before saving.',
      body: [
        {
          type: 'p',
          text: 'When you are grading a written question on a submission, look for Suggest grades with AI. It reads the student\'s answer against your rubric or point value and proposes a grade and comment for each written question, plus overall feedback.',
        },
        {
          type: 'p',
          text: 'Nothing saves automatically. Each suggestion has its own Accept button, so you decide question by question whether to use it, adjust it, or grade it yourself from scratch.',
        },
        {
          type: 'note',
          text: 'It only grades written answers. Multiple choice, true or false, and matching questions are already graded automatically and are not affected.',
        },
      ],
    },
  ],
};

export default academy;
